import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PersonIcon from '@mui/icons-material/Person';
import { Stack, Typography, useTheme } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import WalletSelector from '../../../features/Transactions/WalletSelector';
import IconCircleWrapper from '../../../ui/IconCircleWrapper';
import { CaretRightIcon } from '../../../ui/icons';
import DatePickerComponent from './DatePickerComponent';
import DescriptionField from './DescriptionField';

interface Wallet {
  title: string;
  id: string;
  wallet: {
    id: string;
    name: string;
    balance: number;
    type: string;
  };
}

interface CollectTransactionFormProps {
  amount: number | null;
  setLoanWallet: (wallet: Wallet | null) => void;
  setRepayWallet: (wallet: Wallet | null) => void;
  setDatePicker: (date: Dayjs | undefined) => void;
  setDescription: (description: string) => void;
  // Initial values for editing
  initialLoanWallet?: Wallet | null;
  initialRepayWallet?: Wallet | null;
  initialDatePicker?: Dayjs | undefined;
  initialDescription?: string;
}

type Step = {
  condition: () => boolean;
  action: () => void;
};

const CollectTransactionForm = (props: CollectTransactionFormProps) => {
  const theme = useTheme();
  const [firstRender, setFirstRender] = useState(true);
  const { t } = useTranslation(['common', 'transactions', 'loan']);
  const descriptionInputRef = useRef<HTMLInputElement>(null);
  const { transactionId } = useParams<{ transactionId: string }>();
  const [loanWallet, setLoanWallet] = useState<Wallet | null>(props.initialLoanWallet || null);
  const [repayWallet, setRepayWallet] = useState<Wallet | null>(props.initialRepayWallet || null);
  const [datePicker, setDatePicker] = useState<Dayjs | undefined>(props.initialDatePicker || dayjs());
  const [description, setDescription] = useState<string>(props.initialDescription || '');
  const [openDatePicker, setOpenDatePicker] = useState<boolean>(false);
  const [openDialogSelectLoanWallets, setOpenDialogSelectLoanWallets] =
    useState<boolean>(false);
  const [openDialogSelectRepayWallets, setOpenDialogSelectRepayWallets] =
    useState<boolean>(false);

  const isUpdate = !!transactionId;

  const handleOnCloseDialog = () => {
    setOpenDialogSelectLoanWallets(false);
    setOpenDialogSelectRepayWallets(false);
  };

  const stepsConfig: Step[] = [
    {
      condition: () => !!props.amount,
      action: () => null,
    },
    {
      condition: () => !!loanWallet,
      action: () => setOpenDialogSelectLoanWallets(true),
    },
    {
      condition: () => !!repayWallet,
      action: () => setOpenDialogSelectRepayWallets(true),
    },
    {
      condition: () => !!datePicker,
      action: () => setOpenDatePicker(true),
    },
    {
      condition: () => !!description,
      action: () => {
        descriptionInputRef.current?.focus();
        descriptionInputRef.current?.click();
      },
    }
  ];

  useEffect(() => {
    if (firstRender) {
      setFirstRender(false);
    }

    if (isUpdate) return;

    for (const step of stepsConfig) {
      if (!step.condition()) {
        step.action();
        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.amount, loanWallet, repayWallet, datePicker, isUpdate, firstRender]);

  // Update internal state when initial values change
  useEffect(() => {
    if (props.initialLoanWallet !== undefined) {
      setLoanWallet(props.initialLoanWallet);
    }
  }, [props.initialLoanWallet]);

  useEffect(() => {
    if (props.initialRepayWallet !== undefined) {
      setRepayWallet(props.initialRepayWallet);
    }
  }, [props.initialRepayWallet]);

  useEffect(() => {
    if (props.initialDatePicker !== undefined) {
      setDatePicker(props.initialDatePicker);
    }
  }, [props.initialDatePicker]);

  useEffect(() => {
    if (props.initialDescription !== undefined) {
      setDescription(props.initialDescription);
    }
  }, [props.initialDescription]);

  return (
    <>
      <Stack spacing={4} width="100%">
        {/* Loan Wallet */}
        <Stack
          direction="row"
          spacing={theme.spacing(2)}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={theme.spacing(2)} alignItems="center">
            <IconCircleWrapper size={30} bgcolor={theme.palette.navy[800]} color={theme.palette.common.white}>
              <PersonIcon sx={{ fontSize: 18 }} />
            </IconCircleWrapper>
          </Stack>
          <Stack
            borderBottom={`1px solid ${theme.palette.divider}`}
            width="100%"
            alignItems="end"
            direction="row"
            justifyContent="space-between"
            paddingBottom={theme.spacing(2)}
            onClick={() => setOpenDialogSelectLoanWallets(true)}
            sx={{ cursor: 'pointer' }}
          >
            <Typography variant="body1" color="secondary">
              {t('transaction_form.debt_collection.select_debtor', {
                ns: 'loan',
              })}
            </Typography>
            {loanWallet?.wallet ? (
              <Typography variant="h3">
                {loanWallet.wallet.name}
              </Typography>
            ) : (
              <CaretRightIcon style={{ fontSize: 24, color: theme.palette.text.primary }} />
            )}
          </Stack>
        </Stack>

        {/* Repay Wallet */}
        <Stack
          direction="row"
          spacing={theme.spacing(2)}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={theme.spacing(2)} alignItems="center">
            <IconCircleWrapper size={30} bgcolor={theme.palette.navy[800]} color={theme.palette.common.white}>
              <AccountBalanceWalletIcon sx={{ fontSize: 18 }} />
            </IconCircleWrapper>
          </Stack>
          <Stack
            borderBottom={`1px solid ${theme.palette.divider}`}
            width="100%"
            alignItems="end"
            direction="row"
            justifyContent="space-between"
            paddingBottom={theme.spacing(2)}
            onClick={() => setOpenDialogSelectRepayWallets(true)}
            sx={{ cursor: 'pointer' }}
          >
            <Typography variant="body1" color="secondary">
              {t('transaction_form.debt_collection.destination_wallet', {
                ns: 'loan',
              })}
            </Typography>
            {repayWallet?.wallet ? (
              <Typography variant="h3">
                {repayWallet.wallet.name}
              </Typography>
            ) : (
              <CaretRightIcon style={{ fontSize: 24, color: theme.palette.text.primary }} />
            )}
          </Stack>
        </Stack>

        {/* Date */}
        <DatePickerComponent
          datePicker={datePicker}
          setDatePicker={(date: Dayjs | undefined) => {
            setDatePicker(date);
            props.setDatePicker(date);
          }}
          openDatePicker={openDatePicker}
          setOpenDatePicker={setOpenDatePicker}
          clearable={false}
        />

        {/* Description */}
        <DescriptionField
          description={description}
          setDescription={(value: string) => {
            setDescription(value);
            props.setDescription(value);
          }}
          ref={descriptionInputRef}
        />
      </Stack>

      <WalletSelector
        open={openDialogSelectLoanWallets}
        onClose={handleOnCloseDialog}
        onSelectWallet={(data: any) => {
          setLoanWallet(data);
          props.setLoanWallet(data);
          setOpenDialogSelectLoanWallets(false);
        }}
        walletTypes={['LOAN']}
        customTitle={t('transaction_form.debt_collection.select_debtor', { ns: 'loan', defaultValue: 'Bạn thu nợ từ ai?' })}
      />

      <WalletSelector
        open={openDialogSelectRepayWallets}
        onClose={handleOnCloseDialog}
        onSelectWallet={(data: any) => {
          setRepayWallet(data);
          props.setRepayWallet(data);
          setOpenDialogSelectRepayWallets(false);
        }}
        walletTypes={['BASIC']}
        customTitle={t('transaction_form.debt_collection.destination_wallet', { ns: 'loan' })}
      />
    </>
  );
};

export default CollectTransactionForm;
